/**
 * The property viewing booking the landing page offers as an example.
 *
 * Chosen for the businesses FlowGate now sells to (letting agents and estate agents run hundreds
 * of these a week over WhatsApp), and built to show the same three things the barber example
 * does: a question that appears only when it applies (pet details), a branch ("none of these
 * suit me" goes somewhere else), and a booking that finishes in the chat. The viewing times are
 * fixed here because an example has no diary behind it; with booking connected they come live.
 *
 * The JSON is compiled output, what the builder itself writes from these screens. The test holds
 * it to that: hydrate then compile must return it unchanged, so it can never drift into something
 * the builder would not have written.
 */
export const VIEWING_BOOKING = {
  id: 'viewing-booking',
  brief: 'Viewing bookings for a letting agent: which property, a viewing time, pets, and the applicant’s details',
  reply: 'Here is a three-step viewing booking: the property, then a time, then their details. Pet details only appear if they say they have one, and “none of these suit me” takes them to a call-back instead. With booking connected, the times come live from your diary. Tell me your properties and I will swap them in, or send it to your phone and book as an applicant would.',
  name: 'Property viewings',
  flowJson: {
  "version": "7.3",
  "screens": [
    {
      "id": "screen_a",
      "title": "Book a viewing",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextHeading",
            "text": "Which property?"
          },
          {
            "type": "TextBody",
            "text": "Pick the home you would like to see and we will find you a time."
          },
          {
            "type": "RadioButtonsGroup",
            "name": "property",
            "label": "Property",
            "required": true,
            "data-source": [
              {
                "id": "kelham_2bed",
                "title": "2-bed flat, Kelham Island, £1,150 pcm"
              },
              {
                "id": "crookes_3bed",
                "title": "3-bed house, Crookes, £1,450 pcm"
              },
              {
                "id": "centre_1bed",
                "title": "1-bed flat, City Centre, £895 pcm"
              }
            ]
          },
          {
            "type": "RadioButtonsGroup",
            "name": "pets",
            "label": "Any pets?",
            "required": true,
            "data-source": [
              {
                "id": "no",
                "title": "No"
              },
              {
                "id": "yes",
                "title": "Yes"
              }
            ]
          },
          {
            "type": "If",
            "condition": "${form.pets} == 'yes'",
            "then": [
              {
                "type": "TextInput",
                "name": "pet_details",
                "label": "What pets?",
                "input-type": "text",
                "required": false
              }
            ]
          },
          {
            "type": "Footer",
            "label": "Continue",
            "on-click-action": {
              "name": "navigate",
              "next": {
                "type": "screen",
                "name": "screen_b"
              }
            }
          }
        ]
      }
    },
    {
      "id": "screen_b",
      "title": "Viewing time",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextHeading",
            "text": "When can you come?"
          },
          {
            "type": "RadioButtonsGroup",
            "name": "slot",
            "label": "Thursday",
            "required": true,
            "data-source": [
              {
                "id": "t1000",
                "title": "10:00"
              },
              {
                "id": "t1230",
                "title": "12:30"
              },
              {
                "id": "t1730",
                "title": "17:30"
              },
              {
                "id": "none",
                "title": "None of these suit me"
              }
            ]
          },
          {
            "type": "If",
            "condition": "${form.slot} == 'none'",
            "then": [
              {
                "type": "Footer",
                "label": "Continue",
                "on-click-action": {
                  "name": "navigate",
                  "next": {
                    "type": "screen",
                    "name": "screen_d"
                  }
                }
              }
            ],
            "else": [
              {
                "type": "Footer",
                "label": "Continue",
                "on-click-action": {
                  "name": "navigate",
                  "next": {
                    "type": "screen",
                    "name": "screen_c"
                  }
                }
              }
            ]
          }
        ]
      }
    },
    {
      "id": "screen_c",
      "title": "Your details",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Your name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "phone",
            "label": "Mobile number",
            "input-type": "phone",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "email",
            "label": "Email",
            "input-type": "email",
            "required": true
          },
          {
            "type": "DatePicker",
            "name": "move_in",
            "label": "When would you move in?",
            "required": false
          },
          {
            "type": "Footer",
            "label": "Book viewing",
            "on-click-action": {
              "name": "complete",
              "payload": {
                "property": "${screen.screen_a.form.property}",
                "pets": "${screen.screen_a.form.pets}",
                "pet_details": "${screen.screen_a.form.pet_details}",
                "slot": "${screen.screen_b.form.slot}",
                "full_name": "${form.full_name}",
                "phone": "${form.phone}",
                "email": "${form.email}",
                "move_in": "${form.move_in}"
              }
            }
          }
        ]
      },
      "terminal": true
    },
    {
      "id": "screen_d",
      "title": "We will call you",
      "layout": {
        "type": "SingleColumnLayout",
        "children": [
          {
            "type": "TextBody",
            "text": "No problem. Leave your number and we will call to arrange a time."
          },
          {
            "type": "TextInput",
            "name": "full_name",
            "label": "Your name",
            "input-type": "text",
            "required": true
          },
          {
            "type": "TextInput",
            "name": "phone",
            "label": "Mobile number",
            "input-type": "phone",
            "required": true
          },
          {
            "type": "Footer",
            "label": "Ask them to call me",
            "on-click-action": {
              "name": "complete",
              "payload": {
                "property": "${screen.screen_a.form.property}",
                "pets": "${screen.screen_a.form.pets}",
                "pet_details": "${screen.screen_a.form.pet_details}",
                "slot": "${screen.screen_b.form.slot}",
                "full_name": "${form.full_name}",
                "phone": "${form.phone}"
              }
            }
          }
        ]
      },
      "terminal": true
    }
  ],
  "routing_model": {
    "screen_a": [
      "screen_b"
    ],
    "screen_b": [
      "screen_d",
      "screen_c"
    ]
  },
  "_builderMeta": {
    "screenRouting": {
      "screen_a": {
        "mode": "simple",
        "rules": [],
        "defaultNextScreenId": "screen_b"
      },
      "screen_b": {
        "mode": "simple",
        "rules": [
          {
            "mode": "simple",
            "fieldName": "slot",
            "operator": "eq",
            "value": "none",
            "nextScreenId": "screen_d"
          }
        ],
        "defaultNextScreenId": "screen_c"
      }
    },
    "componentConditions": {
      "screen_a:4": {
        "mode": "simple",
        "field": "pets",
        "operator": "eq",
        "value": "yes"
      }
    }
  }
  },
};
